/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import {
  Evaluator,
  EvaluatorForStudent,
  JobDetails,
  Mentee,
  MenteeAssignment,
  Mentor,
  MentorshipImportReport,
  MentorshipStatus,
  Student,
  StudentAssignment,
  StudentForEvaluator,
  StudentImportReport,
} from '@app/app/page-modules/admin/components/mentors/admin-mentors.model';
import {
  MyRelationItem,
  MyRelationsCounts,
  MyRelationsDetails,
  MyRelationsFilter,
} from '@app/app/page-modules/reviewer-dashboard/models/my-relations.model';
import { CommentRequest } from '@app/app/page-modules/reviews/models';
import { MembersBooleanSearchRequest } from '@app/app/shared/models/admin/boolean-filters.model';
import { UserSearch } from '@app/app/shared/models/admin/members.model';
import { Page } from '@app/app/shared/models/page';
import { ObservableResult } from '@app/app/shared/store';
import { Observable, of } from 'rxjs';
import {
  CreateJobRequest,
  MenteePlaylistSummary,
  MentorFeedback,
  MentorFeedbackEventNote,
  MentorFeedbackSummary,
  MentorMenteeDataService,
  PendingMenteesDecisionRequest,
  RelatedUserSearchItem,
} from './mentor-mentee-data.service';

@Injectable()
export class MockMentorMenteeDataService implements MentorMenteeDataService {
  getAllJobs(): ObservableResult<JobDetails[]> {
    return ObservableResult.ofSuccess();
  }
  createJob(request: CreateJobRequest): ObservableResult<JobDetails> {
    return ObservableResult.ofSuccess();
  }
  assignJobToStudentEvaluator(evaluatorUserUid: string, studentUserUid: string, jobId: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }
  removeJobFromStudentEvaluator(evaluatorUserUid: string, studentUserUid: string, jobId: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }
  searchByMentor(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Mentor>> {
    return ObservableResult.ofSuccess();
  }

  searchByMentee(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Mentee>> {
    return ObservableResult.ofSuccess();
  }

  searchByEvaluator(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Evaluator>> {
    return ObservableResult.ofSuccess();
  }

  searchByStudent(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Student>> {
    return ObservableResult.ofSuccess();
  }

  findStudentsForEvaluator(
    page: number,
    size: number,
    evaluatorUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<StudentForEvaluator>> {
    return ObservableResult.ofSuccess();
  }

  findEvaluatorsForStudent(
    page: number,
    size: number,
    studentUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<EvaluatorForStudent>> {
    return ObservableResult.ofSuccess();
  }

  findMenteesForMentor(
    page: number,
    size: number,
    mentorUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<Mentee>> {
    return ObservableResult.ofSuccess();
  }

  findMentorsForMentee(
    page: number,
    size: number,
    menteeUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<Mentor>> {
    return ObservableResult.ofSuccess();
  }

  findMentors(page: number, size: number, term?: string, statuses?: MentorshipStatus[]): ObservableResult<Page<Mentor>> {
    return ObservableResult.ofSuccess();
  }

  assignMenteeToMentor(mentorUserUid: string, menteeUserUid: string, status: MentorshipStatus): ObservableResult<MenteeAssignment> {
    return ObservableResult.ofSuccess();
  }

  deleteMenteeFromMentor(mentorUserUid: string, menteeUserUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  assignStudentToEvaluator(
    evaluatorUserUid: string,
    studentUserUid: string,
    status: MentorshipStatus,
  ): ObservableResult<StudentAssignment> {
    return ObservableResult.ofSuccess();
  }

  deleteStudentFromEvaluator(evaluatorUserUid: string, studentUserUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  importMentees(menteesFile: File, sendNotifications: boolean): ObservableResult<MentorshipImportReport> {
    return ObservableResult.ofSuccess();
  }

  importStudents(studentsFile: File, sendNotifications: boolean): ObservableResult<StudentImportReport> {
    return ObservableResult.ofSuccess();
  }

  postPendingMenteesDecision(decision: PendingMenteesDecisionRequest): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  findPendingMentees(): ObservableResult<UserSearch[]> {
    return ObservableResult.ofSuccess();
  }

  getAllFeedbackForLearnerPlaylist(playlistUid: string, learnerUserUid: string): ObservableResult<MentorFeedback[]> {
    return ObservableResult.ofSuccess();
  }

  updateNoteOnForm(
    playlistUid: string,
    cardUid: string,
    formUid: string,
    learnerUserUid: string,
    noteUid: string,
    request: CommentRequest,
  ): ObservableResult<MentorFeedbackEventNote> {
    return ObservableResult.ofSuccess();
  }
  createNoteOnForm(
    playlistUid: string,
    cardUid: string,
    formUid: string,
    learnerUserUid: string,
    request: CommentRequest,
  ): ObservableResult<MentorFeedbackEventNote> {
    return ObservableResult.ofSuccess();
  }
  getSingleMentorFeedbackForLearnerPlaylist(
    playlistUid: string,
    learnerUserUid: string,
    mentorUserUid: string,
  ): ObservableResult<MentorFeedback> {
    return ObservableResult.ofSuccess();
  }
  deleteNoteOnForm(playlistUid: string, cardUid: string, formUid: string, learnerUserUid: string, noteUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }
  upsertPlaylistSummary(playlistUid: string, learnerUserUid: string, request: CommentRequest): ObservableResult<MentorFeedbackSummary> {
    return ObservableResult.ofSuccess();
  }
  deletePlaylistSummary(playlistUid: string, learnerUserUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }
  getSubmissionsFromMentee(
    mentorUserUid: string,
    menteeUserUid: string,
    page: number,
    size: number,
  ): ObservableResult<Page<MenteePlaylistSummary>> {
    return ObservableResult.ofSuccess();
  }
  getMentee(menteeUserUid: string): ObservableResult<Mentee> {
    return ObservableResult.ofSuccess();
  }
  patchFormAcknowledgement(
    playlistUid: string,
    cardUid: string,
    formUid: string,
    menteeUserUid: string,
    isAcknowledged: boolean,
  ): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }
  getRelatedUser(relatedUserUid: string): ObservableResult<RelatedUserSearchItem> {
    return ObservableResult.ofSuccess();
  }

  getMyRelations(filter: MyRelationsFilter | null, term: string | null, page: number, size: number): Observable<MyRelationsDetails> {
    const counts: MyRelationsCounts = {
      total: MOCK_MY_RELATIONS.length,
      needsAction: MOCK_MY_RELATIONS.filter((row) => row.totalActions > 0).length,
      totalActions: MOCK_MY_RELATIONS.reduce((total, row) => total + row.totalActions, 0),
      mentees: MOCK_MY_RELATIONS.filter((row) => row.roles.includes('MENTEE')).length,
      students: MOCK_MY_RELATIONS.filter((row) => row.roles.includes('STUDENT')).length,
      pendingRequests: MOCK_MY_RELATIONS.filter((row) => row.actions.pendingRequest).length,
    };
    const appliedFilter = filter ?? (counts.needsAction > 0 ? 'NEEDS_ACTION' : 'ALL');
    const needle = term?.trim().toLowerCase();
    const rows = MOCK_MY_RELATIONS.filter((row) => {
      switch (appliedFilter) {
        case 'NEEDS_ACTION':
          return row.totalActions > 0;
        case 'MENTEES':
          return row.roles.includes('MENTEE');
        case 'STUDENTS':
          return row.roles.includes('STUDENT');
        default:
          return true;
      }
    }).filter((row) => !needle || `${row.firstName} ${row.lastName} ${row.email}`.toLowerCase().includes(needle));

    return of({
      relations: {
        content: rows.slice(page * size, (page + 1) * size),
        totalNumberOfElement: rows.length,
        pageSize: size,
        currentPageNumber: page,
        numberOfPages: Math.ceil(rows.length / size),
      },
      counts,
      filter: appliedFilter,
    });
  }
}

const daysAgo = (days: number): string => new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

const MOCK_JOB: JobDetails = {
  _id: 'job-1',
  hireId: 'hire-1',
  title: 'Clinical placement',
  level: '2',
  startDate: daysAgo(90),
};

const NO_ACTIONS = { pendingRequest: false, unacknowledgedForms: 0, pendingReviews: 0, feedbackToProvide: 0 };

// Ordered as the API orders them: pending requests, then needs action oldest first, then everyone else by name.
const MOCK_MY_RELATIONS: MyRelationItem[] = [
  {
    userUid: 'mock-pending',
    firstName: 'Daniel',
    lastName: 'Osei',
    email: 'daniel.osei@example.com',
    roles: ['MENTEE'],
    menteeStatus: 'PENDING',
    studentHasActiveJob: false,
    jobs: [],
    actions: { ...NO_ACTIONS, pendingRequest: true },
    totalActions: 1,
    nextAction: { type: 'MENTEE_REQUEST', since: daysAgo(2) },
    oldestPendingSince: daysAgo(2),
  },
  {
    userUid: 'mock-both',
    firstName: 'Tomás',
    lastName: 'Barros',
    email: 'tomas.barros@example.com',
    roles: ['MENTEE', 'STUDENT'],
    menteeStatus: 'ACTIVE',
    studentHasActiveJob: true,
    jobs: [MOCK_JOB],
    actions: { ...NO_ACTIONS, unacknowledgedForms: 2, pendingReviews: 1 },
    totalActions: 3,
    nextAction: {
      type: 'REVIEW',
      since: daysAgo(9),
      itemType: 'PLAYLIST',
      itemUid: 'playlist-1',
      itemUri: 'clinical-placement-report',
      itemTitle: 'Clinical placement report',
    },
    oldestPendingSince: daysAgo(9),
  },
  {
    userUid: 'mock-mentee',
    firstName: 'Maya',
    lastName: 'Asante',
    email: 'maya.asante@example.com',
    roles: ['MENTEE'],
    menteeStatus: 'ACTIVE',
    studentHasActiveJob: false,
    jobs: [],
    actions: { ...NO_ACTIONS, unacknowledgedForms: 1 },
    totalActions: 1,
    nextAction: {
      type: 'MENTEE_FORM',
      since: daysAgo(1),
      itemType: 'PLAYLIST',
      itemUid: 'playlist-2',
      itemUri: 'reflective-log-term-1',
      itemTitle: 'Reflective log · Term 1',
      cardUid: 'card-1',
      formUid: 'form-1',
    },
    oldestPendingSince: daysAgo(1),
  },
  {
    userUid: 'mock-student',
    firstName: 'Sana',
    lastName: 'Lindqvist',
    email: 'sana.lindqvist@example.com',
    roles: ['STUDENT'],
    studentHasActiveJob: true,
    jobs: [MOCK_JOB],
    actions: { ...NO_ACTIONS, feedbackToProvide: 1 },
    totalActions: 1,
    nextAction: {
      type: 'FEEDBACK_360',
      since: daysAgo(0),
      itemType: 'CARD',
      itemUid: 'assessment-1',
      formattedUri: '/cards/360-feedback',
      itemTitle: '360 feedback',
    },
    oldestPendingSince: daysAgo(0),
  },
  {
    userUid: 'mock-quiet',
    firstName: 'Leah',
    lastName: 'Park',
    email: 'leah.park@example.com',
    roles: ['STUDENT'],
    studentHasActiveJob: true,
    jobs: [MOCK_JOB],
    actions: NO_ACTIONS,
    totalActions: 0,
  },
];
